import { Box, Card, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';
import { PrizeDto, PrizeWrapper } from 'src/util/types';

type PossiblePrizesProps = {
  prizes?: PrizeWrapper[];
};

export default function PossiblePrizes({ prizes: prizesFromProps }: PossiblePrizesProps) {
  const authContext = useAuth();
  const [prizes, setPrizes] = useState<PrizeDto[]>([]);
  const accentStyles = [
    { gradient: 'linear-gradient(135deg, #ff8a00, #e52e71)', glow: 'rgba(229,46,113,0.45)' },
    { gradient: 'linear-gradient(135deg, #00c6ff, #0072ff)', glow: 'rgba(0,114,255,0.4)' },
    { gradient: 'linear-gradient(135deg, #8e2de2, #4a00e0)', glow: 'rgba(142,45,226,0.4)' },
    { gradient: 'linear-gradient(135deg, #36d1dc, #5b86e5)', glow: 'rgba(91,134,229,0.35)' }
  ];

  useEffect(() => {
    if (prizesFromProps && prizesFromProps.length >= 0) {
      const mapped = prizesFromProps
        .map((wrapper) => wrapper?.prize)
        .filter(Boolean) as PrizeDto[];
      setPrizes(mapped);
      return;
    }

    const fetchPrizesList = async () => {
      try {
        const response = await axios.get(config.apiUrl + '/api/prize/all', {
          headers: {
            Authorization: 'Bearer ' + authContext.tokens.accessToken
          }
        });
        setPrizes(response.data.content);
      } catch (err) {
        console.log(err);
      }
    };
    if (authContext.tokens?.accessToken) {
      fetchPrizesList();
    }
  }, [authContext.tokens?.accessToken, prizesFromProps]);

  const sliderItems = useMemo(() => {
    if (prizes.length === 0) {
      return [];
    }
    return [...prizes, ...prizes];
  }, [prizes]);

  return (
    <>
      <Box textAlign="center" mb={2}>
        <Typography variant="h5" sx={{ textTransform: 'uppercase', letterSpacing: 2, color: '#fff' }}>
          Unsere Preise
        </Typography>
      </Box>
      {prizes.length > 0 ? (
        <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 4, px: 0, py: 4 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top, rgba(255,136,0,0.18), transparent 70%)',
              filter: 'blur(30px)'
            }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 0,
              animation: 'slidePrizes 15s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
              '@keyframes slidePrizes': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' }
              }
            }}
          >
            {sliderItems.map((prize, idx) => {
              const accent = accentStyles[idx % accentStyles.length];
              return (
                <Card
                  key={`${prize.id}-${idx}`}
                  sx={{
                    width: 180,
                    flex: '0 0 180px',
                    minHeight: 180,
                    borderRadius: 6,
                    background: 'rgba(10,10,15,0.9)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: `0 22px 40px ${accent.glow}`,
                    transition: 'transform 0.5s ease, box-shadow 0.5s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    mr: 2,
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.03)',
                      boxShadow: `0 32px 65px ${accent.glow}`
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: accent.gradient,
                      opacity: 0.25
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      borderRadius: 4,
                      overflow: 'hidden',
                      m: 2,
                      position: 'relative'
                    }}
                  >
                    <Box
                      component="img"
                      src={prize.image}
                      alt={prize.description}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.95) 95%)'
                      }}
                    />
                  </Box>
                </Card>
              );
            })}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(2,2,2,0.75), transparent 25%, transparent 75%, rgba(2,2,2,0.75))',
              pointerEvents: 'none'
            }}
          />
        </Box>
      ) : (
        <Typography textAlign="center" color="text.secondary">
          Keine Preise verfügbar.
        </Typography>
      )}
    </>
  );
}
